import { useEffect, useId, useState } from "react";
import { useContainers } from "../../api/queries";
import { EmptyState } from "../../components/AsyncStates";
import { LiveLogPanel } from "../containers/LiveLogPanel";
import "./services.css";

interface ServiceLiveLogsSectionProps {
  serviceId: string;
}

/**
 * "Live logs on the same screen" (IA slice 4, ServiceRollout board) — reuses
 * `LiveLogPanel` (the same live-tail component `ContainerDetailPage` uses) rather than
 * reimplementing streaming here; a service can back more than one container (one per
 * placement), so this adds only the picker `LiveLogPanel` itself has no opinion about,
 * defaulting to the first container and letting the operator switch (e.g. to the
 * in-flight rollout's newest placement).
 */
export function ServiceLiveLogsSection({ serviceId }: ServiceLiveLogsSectionProps) {
  const { data } = useContainers({ serviceId, limit: 100 });
  const containers = data?.items ?? [];
  const selectId = useId();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Re-seeds the selection whenever the container SET changes shape (a
  // deploy lands a new placement, or the previously selected one goes
  // away) — never leaves the picker pointed at a container that vanished.
  useEffect(() => {
    if (containers.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!containers.some((c) => c.id === selectedId)) {
      setSelectedId(containers[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containers.map((c) => c.id).join(",")]);

  const selected = containers.find((c) => c.id === selectedId);

  return (
    <section className="service-detail__section" aria-labelledby="service-live-logs-heading">
      <div className="service-live-logs__header">
        <h2 id="service-live-logs-heading">Live logs</h2>
        {containers.length > 1 && (
          <label className="service-live-logs__picker" htmlFor={selectId}>
            <span>Container</span>
            <select id={selectId} value={selectedId ?? ""} onChange={(e) => setSelectedId(e.target.value)}>
              {containers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.nodeName})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {selected ? (
        <LiveLogPanel nodeId={selected.nodeId} containerId={selected.id} />
      ) : (
        <EmptyState
          title="Nothing running yet"
          description="Once this service is running on a machine, its live logs are reachable here."
        />
      )}
    </section>
  );
}
